/**
 * Available target channels for a billing-channel conversion.
 *
 * Provider-agnostic shape so the Phase 3 UI does not hard-code
 * `['stripe', 'shopify']`. When a future channel (Paddle / PayPal / etc.)
 * becomes ready — or when Shopify crosses the Phase B readiness contract
 * (`docs/BILLING.md` → Channel Mobility, Shopify-direction
 * readiness contract`) — updating the `channelReadiness` mapping here is
 * the only change the UI needs. The dropdown automatically surfaces the
 * new option.
 *
 * Mirrors the server-side readiness gate enforced by
 * `lib/billing/channel-conversion/handle.ts` (returns 400
 * `target_channel_not_ready` for an unready channel). Keeping the client
 * preview and the server gate in sync avoids a class of silent UX bugs
 * where the button offers an option the backend immediately rejects.
 *
 * Pure function, no I/O, safe to run on the server or the browser.
 */

import { BILLING_CHANNELS, type BillingChannel } from '../constants'

/**
 * Channel readiness mapping — same policy as the handler's `channelReadiness(...)`.
 * Both Stripe and Shopify are ready: conversion flips the channel to a free/manual
 * shape, and the paid subscribe happens via the channel's hosted checkout (Stripe
 * checkout / Shopify App Pricing plan-selection page). Retained so a future
 * not-yet-ready channel (Paddle / PayPal / etc.) can be added with `false`.
 */
const CHANNEL_READY: Record<BillingChannel, boolean> = {
  [BILLING_CHANNELS.STRIPE]: true,
  [BILLING_CHANNELS.SHOPIFY]: true,
}

/**
 * Return the list of billing channels a tenant on `currentChannel` can be
 * converted TO. Excludes:
 *   - the current channel itself (would return `no_change_needed` at the
 *     backend anyway),
 *   - any channel that fails the readiness gate.
 *
 * Result shape (both channels ready, Phase 2.1):
 *   - currentChannel='shopify' → ['stripe']
 *   - currentChannel='stripe'  → ['shopify']
 *   - currentChannel=null      → ['stripe', 'shopify']
 *
 * Callers use `length === 0` as the signal to hide the "Convert channel"
 * control entirely rather than rendering an empty dropdown.
 */
export function getAvailableTargetChannels(currentChannel: BillingChannel | null): BillingChannel[] {
  const all = Object.values(BILLING_CHANNELS) as BillingChannel[]
  return all.filter((ch) => ch !== currentChannel && CHANNEL_READY[ch])
}
